import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Users,
  FileText,
  MessageSquare,
  Upload,
  Download,
  Eye,
  Search,
  Filter,
  Calendar,
  DollarSign,
  User,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  Send,
  Paperclip,
  ExternalLink,
} from "lucide-react";
import { adminAPI, formatCurrency, formatDate } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const AdministrativeTasks = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState("bonds");
  const [selectedBond, setSelectedBond] = useState(null);
  const [showBondModal, setShowBondModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [newMessage, setNewMessage] = useState({
    recipient: "",
    subject: "",
    content: "",
    attachments: [],
  });

  // Filters
  const [bondStatusFilter, setBondStatusFilter] = useState("all");
  const [bondTypeFilter, setBondTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("all");

  // Mock data for demonstration
  const mockBonds = [
    {
      id: 1,
      bondName: "Malaysia Infrastructure Bond 2024",
      isin: "MY1234567890",
      issuer: "Malaysia Infrastructure Corp",
      productType: "Conventional Bonds",
      status: "Active",
      issueDate: "2024-01-15",
      maturityDate: "2029-01-15",
      nominalAmount: 500000000,
      couponRate: 5.25,
      paymentFrequency: "Semi-annual",
      nextPaymentDate: "2024-12-20",
      totalInvestors: 245,
      lastActivity: "2024-12-15",
    },
    {
      id: 2,
      bondName: "Green Sukuk Series 3",
      isin: "MY0987654321",
      issuer: "Green Energy Holdings",
      productType: "Sukuk",
      status: "Active",
      issueDate: "2024-02-20",
      maturityDate: "2027-02-20",
      nominalAmount: 300000000,
      profitRate: 4.75,
      paymentFrequency: "Quarterly",
      nextPaymentDate: "2024-12-22",
      totalInvestors: 156,
      lastActivity: "2024-12-10",
    },
    {
      id: 3,
      bondName: "Tech Development Bond",
      isin: "MY1122334455",
      issuer: "Tech Innovation Ltd",
      productType: "Conventional Bonds",
      status: "Active",
      issueDate: "2023-06-15",
      maturityDate: "2026-06-15",
      nominalAmount: 150000000,
      couponRate: 6.0,
      paymentFrequency: "Semi-annual",
      nextPaymentDate: "2024-12-25",
      totalInvestors: 89,
      lastActivity: "2024-12-12",
    },
    {
      id: 4,
      bondName: "Infrastructure Bond 2022-A",
      isin: "MY5566778899",
      issuer: "Infrastructure Holdings",
      productType: "Conventional Bonds",
      status: "Matured",
      issueDate: "2022-01-15",
      maturityDate: "2024-12-18",
      nominalAmount: 100000000,
      couponRate: 5.5,
      paymentFrequency: "Semi-annual",
      totalInvestors: 198,
      lastActivity: "2024-12-18",
    },
  ];

  const mockDocuments = [
    {
      id: 1,
      name: "Trust Deed - Malaysia Infrastructure Bond 2024",
      type: "Legal",
      bondId: 1,
      bondName: "Malaysia Infrastructure Bond 2024",
      uploadDate: "2024-01-10",
      uploader: "Legal Department",
      status: "Approved",
      fileSize: "2.1 MB",
      downloadUrl: "#",
    },
    {
      id: 2,
      name: "Shariah Compliance Certificate - Green Sukuk Series 3",
      type: "Shariah",
      bondId: 2,
      bondName: "Green Sukuk Series 3",
      uploadDate: "2024-02-15",
      uploader: "Shariah Committee",
      status: "Approved",
      fileSize: "1.5 MB",
      downloadUrl: "#",
    },
    {
      id: 3,
      name: "Asset Valuation Report - Green Sukuk Series 3",
      type: "Valuation",
      bondId: 2,
      bondName: "Green Sukuk Series 3",
      uploadDate: "2024-02-18",
      uploader: "Valuation Firm",
      status: "Pending Review",
      fileSize: "3.2 MB",
      downloadUrl: "#",
    },
    {
      id: 4,
      name: "Payment Schedule - Tech Development Bond",
      type: "Administrative",
      bondId: 3,
      bondName: "Tech Development Bond",
      uploadDate: "2024-12-01",
      uploader: "Facility Agent",
      status: "Approved",
      fileSize: "890 KB",
      downloadUrl: "#",
    },
  ];

  const mockCommunications = [
    {
      id: 1,
      from: "Legal Counsel",
      to: "Facility Agent",
      subject: "Trust Deed Amendment - Malaysia Infrastructure Bond",
      content:
        "Please review the proposed amendments to the trust deed for the Malaysia Infrastructure Bond 2024...",
      bondId: 1,
      bondName: "Malaysia Infrastructure Bond 2024",
      timestamp: "2024-12-15T14:30:00",
      hasAttachments: true,
      status: "Read",
    },
    {
      id: 2,
      from: "Exchange",
      to: "Facility Agent",
      subject: "Listing Approval Confirmation",
      content:
        "We are pleased to confirm the listing approval for the Green Sukuk Series 3...",
      bondId: 2,
      bondName: "Green Sukuk Series 3",
      timestamp: "2024-12-12T10:15:00",
      hasAttachments: false,
      status: "Read",
    },
    {
      id: 3,
      from: "Custodian",
      to: "Facility Agent",
      subject: "Token Distribution Update",
      content:
        "Token distribution for Tech Development Bond has been completed successfully...",
      bondId: 3,
      bondName: "Tech Development Bond",
      timestamp: "2024-12-10T16:45:00",
      hasAttachments: false,
      status: "Unread",
    },
    {
      id: 4,
      from: "Malaysia Infrastructure Corp",
      to: "Facility Agent",
      subject: "Payment Funding Confirmation",
      content:
        "We confirm that funds for the upcoming coupon payment have been deposited...",
      bondId: 1,
      bondName: "Malaysia Infrastructure Bond 2024",
      timestamp: "2024-12-08T09:20:00",
      hasAttachments: true,
      status: "Read",
    },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      Active: { color: "green", icon: CheckCircle },
      Matured: { color: "gray", icon: Clock },
      Suspended: { color: "red", icon: AlertTriangle },
    };

    const config = statusConfig[status] || { color: "gray", icon: Clock };
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getDocumentStatusBadge = (status) => {
    const statusConfig = {
      Approved: { color: "green", icon: CheckCircle },
      "Pending Review": { color: "yellow", icon: Clock },
      Rejected: { color: "red", icon: AlertTriangle },
    };

    const config = statusConfig[status] || { color: "gray", icon: Clock };
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getFilteredBonds = () => {
    return mockBonds.filter((bond) => {
      const matchesSearch =
        bond.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bond.issuer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bond.isin.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        bondStatusFilter === "all" || bond.status === bondStatusFilter;
      const matchesType =
        bondTypeFilter === "all" || bond.productType === bondTypeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  };

  const getFilteredDocuments = () => {
    return mockDocuments.filter((doc) => {
      const matchesSearch =
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.bondName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = docTypeFilter === "all" || doc.type === docTypeFilter;

      return matchesSearch && matchesType;
    });
  };

  const getFilteredCommunications = () => {
    return mockCommunications.filter((comm) => {
      const matchesSearch =
        comm.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.bondName.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  };

  const handleSendMessage = () => {
    // Simulate sending message
    console.log("Sending message:", newMessage);
    setShowMessageModal(false);
    setNewMessage({ recipient: "", subject: "", content: "", attachments: [] });
  };

  const handleUploadDocument = () => {
    if (!uploadFile) {
      alert("Please select a file to upload");
      return;
    }
    // Simulate document upload
    console.log("Uploading document:", uploadFile);
    setShowUploadModal(false);
    setUploadFile(null);
  };

  const tabs = [
    { id: "bonds", name: "Bond Overview", count: mockBonds.length },
    {
      id: "documents",
      name: "Document Management",
      count: mockDocuments.length,
    },
    {
      id: "communications",
      name: "Communication Log",
      count: mockCommunications.filter((c) => c.status === "Unread").length,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Administrative Tasks
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Bond oversight, document management, and stakeholder
                communications
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    selectedTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.name}
                  {tab.count > 0 && (
                    <span
                      className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                        selectedTab === tab.id
                          ? "bg-primary-100 text-primary-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Bond Overview Tab */}
        {selectedTab === "bonds" && (
          <>
            {/* Filters */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Search */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Bonds
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by bond name, ISIN, or issuer..."
                        className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={bondStatusFilter}
                      onChange={(e) => setBondStatusFilter(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Matured">Matured</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>

                  {/* Bond Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={bondTypeFilter}
                      onChange={(e) => setBondTypeFilter(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Types</option>
                      <option value="Conventional Bonds">
                        Conventional Bonds
                      </option>
                      <option value="Sukuk">Sukuk</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Bond List */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Bond Portfolio ({getFilteredBonds().length})
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bond Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Issuer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount & Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dates
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredBonds().map((bond) => (
                        <tr key={bond.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {bond.bondName}
                              </div>
                              <div className="text-sm text-gray-500">
                                ISIN: {bond.isin}
                              </div>
                              <div className="text-sm text-gray-500">
                                {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <User className="h-4 w-4 text-gray-400 mr-2" />
                              <div className="text-sm text-gray-900">
                                {bond.issuer}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatCurrency(bond.nominalAmount)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {bond.couponRate
                                ? `${bond.couponRate}%`
                                : `${bond.profitRate}%`}{" "}
                              • {bond.paymentFrequency}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              Issue: {formatDate(bond.issueDate)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Maturity: {formatDate(bond.maturityDate)}
                            </div>
                            {bond.nextPaymentDate && (
                              <div className="text-sm text-blue-600">
                                Next: {formatDate(bond.nextPaymentDate)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(bond.status)}
                            <div className="text-xs text-gray-500 mt-1">
                              {bond.totalInvestors} investors
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                setSelectedBond(bond);
                                setShowBondModal(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {getFilteredBonds().length === 0 && (
                    <div className="text-center py-12">
                      <Users className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No bonds found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No bonds match your current filter criteria.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Document Management Tab */}
        {selectedTab === "documents" && (
          <>
            {/* Filters and Actions */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Documents
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by document name or bond..."
                        className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type
                    </label>
                    <select
                      value={docTypeFilter}
                      onChange={(e) => setDocTypeFilter(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All Types</option>
                      <option value="Legal">Legal</option>
                      <option value="Shariah">Shariah</option>
                      <option value="Valuation">Valuation</option>
                      <option value="Administrative">Administrative</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Upload Document
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents List */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Document Repository ({getFilteredDocuments().length})
                  </h3>
                </div>

                <div className="space-y-4">
                  {getFilteredDocuments().map((doc) => (
                    <div
                      key={doc.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <h4 className="text-sm font-medium text-gray-900">
                              {doc.name}
                            </h4>
                            {getDocumentStatusBadge(doc.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-500">
                            <div>Bond: {doc.bondName}</div>
                            <div>Type: {doc.type}</div>
                            <div>Uploaded: {formatDate(doc.uploadDate)}</div>
                            <div>Size: {doc.fileSize}</div>
                          </div>

                          <div className="text-sm text-gray-500 mt-2">
                            Uploaded by: {doc.uploader}
                          </div>
                        </div>

                        <div className="ml-4 flex space-x-2">
                          <a
                            href={doc.downloadUrl}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getFilteredDocuments().length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No documents found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No documents match your current filter criteria.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Communication Log Tab */}
        {selectedTab === "communications" && (
          <>
            {/* Search and Actions */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Communications
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by subject, sender, or bond..."
                        className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => setShowMessageModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      New Message
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Communication List */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Communication Log ({getFilteredCommunications().length})
                  </h3>
                </div>

                <div className="space-y-4">
                  {getFilteredCommunications().map((comm) => (
                    <div
                      key={comm.id}
                      className={`border rounded-lg p-4 ${
                        comm.status === "Unread"
                          ? "border-blue-200 bg-blue-50"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <MessageSquare className="h-5 w-5 text-gray-400" />
                            <h4 className="text-sm font-medium text-gray-900">
                              {comm.subject}
                            </h4>
                            {comm.status === "Unread" && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                New
                              </span>
                            )}
                            {comm.hasAttachments && (
                              <Paperclip className="h-4 w-4 text-gray-400" />
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500 mb-2">
                            <div>From: {comm.from}</div>
                            <div>Bond: {comm.bondName}</div>
                            <div>Date: {formatDate(comm.timestamp)}</div>
                          </div>

                          <p className="text-sm text-gray-700 line-clamp-2">
                            {comm.content}
                          </p>
                        </div>

                        <div className="ml-4">
                          <button className="text-indigo-600 hover:text-indigo-900">
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getFilteredCommunications().length === 0 && (
                    <div className="text-center py-12">
                      <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No communications found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No communications match your search criteria.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bond Detail Modal */}
      {showBondModal && selectedBond && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Bond Details
                </h3>
                <button
                  onClick={() => setShowBondModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Bond Information
                  </h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Bond Name</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.bondName}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">ISIN</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.isin}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Issuer</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.issuer}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Bond Type</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.productType}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Nominal Amount</dt>
                      <dd className="text-sm text-gray-900">
                        {formatCurrency(selectedBond.nominalAmount)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Terms & Schedule
                  </h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Rate</dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.couponRate
                          ? `${selectedBond.couponRate}%`
                          : `${selectedBond.profitRate}%`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">
                        Payment Frequency
                      </dt>
                      <dd className="text-sm text-gray-900">
                        {selectedBond.paymentFrequency}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Issue Date</dt>
                      <dd className="text-sm text-gray-900">
                        {formatDate(selectedBond.issueDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Maturity Date</dt>
                      <dd className="text-sm text-gray-900">
                        {formatDate(selectedBond.maturityDate)}
                      </dd>
                    </div>
                    {selectedBond.nextPaymentDate && (
                      <div>
                        <dt className="text-xs text-gray-500">Next Payment</dt>
                        <dd className="text-sm text-gray-900">
                          {formatDate(selectedBond.nextPaymentDate)}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Upload Document
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type
                  </label>
                  <select className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500">
                    <option value="">Select type...</option>
                    <option value="Legal">Legal Documents</option>
                    <option value="Shariah">Shariah Compliance</option>
                    <option value="Valuation">Valuation Report</option>
                    <option value="Administrative">Administrative</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Associated Bond
                  </label>
                  <select className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500">
                    <option value="">Select bond...</option>
                    {mockBonds.map((bond) => (
                      <option key={bond.id} value={bond.id}>
                        {bond.bondName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadDocument}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                New Message
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient
                  </label>
                  <select
                    value={newMessage.recipient}
                    onChange={(e) =>
                      setNewMessage({
                        ...newMessage,
                        recipient: e.target.value,
                      })
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select recipient...</option>
                    <option value="legal">Legal Counsel</option>
                    <option value="exchange">Exchange</option>
                    <option value="custodian">Custodian</option>
                    <option value="issuer">Issuer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) =>
                      setNewMessage({ ...newMessage, subject: e.target.value })
                    }
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Message subject..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={newMessage.content}
                    onChange={(e) =>
                      setNewMessage({ ...newMessage, content: e.target.value })
                    }
                    rows={6}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Type your message..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdministrativeTasks;
